import React, { useEffect, useState } from "react";
import {
  Edit3,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import useBulkOptionGroups from "../../services/Order/useBulkOptionGroups";
import useBulkCatalogue from "../../services/Order/useBulkCatalogue";

const EMPTY_FORM = {
  option_group_id: "",
  restaurant_id: "",
  group_name: "",
  description: "",
  status: "ACTIVE",
};

const fieldClass =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

export default function BulkOptionGroups() {
  const {
    optionGroupsLoading,
    optionGroupSaving,
    optionGroupDeletingId,
    getOptionGroups,
    insertOptionGroup,
    updateOptionGroup,
    deleteOptionGroup,
  } = useBulkOptionGroups();

  const { getRestaurants } = useBulkCatalogue();

 
const [rows, setRows] = useState([]);
const [restaurants, setRestaurants] = useState([]);
const [form, setForm] = useState(EMPTY_FORM);
const [modalOpen, setModalOpen] = useState(false);

const [optionGroupSearch, setOptionGroupSearch] = useState("");

const filteredOptionGroups = rows.filter((group) => {
  const normalize = (value) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const query = normalize(optionGroupSearch.trim());

  if (!query) return true;

  const searchableFields = [
    group?.option_group_id,
    group?.group_name,
    group?.restaurant_id,
    group?.description,
    group?.status,
  ];

  return searchableFields.some((value) => normalize(value).includes(query));
});


  
const ssoUsername = localStorage.getItem("anlook_sso_username") || "";

const normalizeName = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/marketting/g, "marketing");

const getFixedRestaurant = (rows) =>
  rows.find(
    (r) => normalizeName(r?.restaurant_name) === normalizeName(ssoUsername),
  );


  const load = async () => {
    const groupRows = await getOptionGroups("");
    setRows(Array.isArray(groupRows) ? groupRows : []);
  };
  const ensureRestaurants = async () => {
    if (restaurants.length) return restaurants;
    const r = await getRestaurants("");
    const next = Array.isArray(r) ? r : [];
    setRestaurants(next);
    return next;
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

 
const openAdd = async () => {
  const rows = await ensureRestaurants();
  const restaurant = getFixedRestaurant(rows);

  if (!restaurant) {
    alert("SSO restaurant not found.");
    return;
  }

  setForm({
    ...EMPTY_FORM,
    restaurant_id: String(restaurant.restaurant_id ?? restaurant.id ?? ""),
  });

  setModalOpen(true);
};



const openEdit = async (row) => {
  const rows = await ensureRestaurants();
  const restaurant = getFixedRestaurant(rows);

  if (!restaurant) {
    alert("SSO restaurant not found.");
    return;
  }

  const restaurantId = String(restaurant.restaurant_id ?? restaurant.id ?? "");

  if (String(row?.restaurant_id ?? "") !== restaurantId) {
    alert("This option group belongs to another restaurant.");
    return;
  }

  setForm({
    option_group_id: String(row?.option_group_id ?? row?.id ?? ""),
    restaurant_id: restaurantId,
    group_name: String(row?.group_name ?? ""),
    description: String(row?.description ?? ""),
    status: String(row?.status ?? "ACTIVE"),
  });

  setModalOpen(true);
};


  const closeModal = () => {
    if (optionGroupSaving) return;
    setModalOpen(false);
    setForm(EMPTY_FORM);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const saved = form.option_group_id
      ? await updateOptionGroup(form)
      : await insertOptionGroup(form);

    if (saved) {
      closeModal();
      await load();
    }
  };

  const handleDelete = async (row) => {
    const id = row?.option_group_id ?? row?.id;
    if (!id) return;

    const deleted = await deleteOptionGroup(id);
    if (deleted) await load();
  };

  const restaurantName = (id) =>
    restaurants.find(
      (restaurant) =>
        String(restaurant?.restaurant_id ?? restaurant?.id ?? "") ===
        String(id ?? ""),
    )?.restaurant_name || `Restaurant #${id || "—"}`;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Option Groups
          </h3>
          <p className="text-[11px] text-slate-500">
            Manage restaurant option groups.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* OPTION GROUP SEARCH */}
          <div className="relative w-full sm:w-64 lg:w-80">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={optionGroupSearch}
              onChange={(e) => setOptionGroupSearch(e.target.value)}
              placeholder="Search group ID, name, description..."
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />

            {optionGroupSearch && (
              <button
                type="button"
                onClick={() => setOptionGroupSearch("")}
                className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={load}
            disabled={optionGroupsLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={optionGroupsLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>

          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700"
          >
            <Plus size={13} />
            Add Option Group
          </button>
        </div>
      </div>

      {optionGroupsLoading ? (
        <div className="h-24 animate-pulse rounded-xl border border-slate-200 bg-white" />
      ) : filteredOptionGroups.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500">
          {optionGroupSearch.trim()
            ? "No option groups match your search."
            : "No option groups found."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {filteredOptionGroups.map((row, index) => {
            const id = row?.option_group_id ?? row?.id ?? index;
            const deleting = optionGroupDeletingId === String(id);

            return (
              <div
                key={id}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">
                      {row?.group_name || "Unnamed Option Group"}
                    </p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                        String(row?.status || "").toUpperCase() === "ACTIVE"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-rose-100 text-rose-700"
                      }`}
                    >
                      {row?.status || "—"}
                    </span>
                  </div>

                  <p className="mt-1 text-[11px] text-slate-500">
                    ID #{id} · {restaurantName(row?.restaurant_id)}
                  </p>

                  {row?.description ? (
                    <p className="mt-1 line-clamp-2 text-xs text-slate-600">
                      {row.description}
                    </p>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600"
                  title="Edit"
                >
                  <Edit3 size={13} />
                </button>

                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => handleDelete(row)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 disabled:opacity-50"
                  title="Delete"
                >
                  {deleting ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Trash2 size={13} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {form.option_group_id
                    ? "Edit Option Group"
                    : "Add Option Group"}
                </h3>
                <p className="text-xs text-slate-500">
                  Restaurant option group details
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={optionGroupSaving}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Restaurant
                  </span>

                  <input
                    type="text"
                    value={ssoUsername}
                    readOnly
                    className="h-10 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none"
                  />
                </label>

                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Group Name
                  </span>
                  <input
                    name="group_name"
                    value={form.group_name}
                    onChange={handleChange}
                    disabled={optionGroupSaving}
                    className={fieldClass}
                    required
                  />
                </label>

                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={optionGroupSaving}
                    className={fieldClass}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>

                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </span>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    disabled={optionGroupSaving}
                    className="min-h-24 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none focus:border-emerald-400"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={optionGroupSaving}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={
                    optionGroupSaving ||
                    !form.restaurant_id ||
                    !form.group_name.trim()
                  }
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {optionGroupSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : form.option_group_id ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}

                  {optionGroupSaving
                    ? "Saving..."
                    : form.option_group_id
                      ? "Update Option Group"
                      : "Add Option Group"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
